import React, { useState, useEffect, useRef } from "react";
import { ToastMessage, showToast } from "../../Shared/ToastMessage";
import axios from "axios";
import { localserver } from "../../../api/common";
import EventInvoice from "./invoice/invoice-web";
import { useUser } from "../../AdminUsers/UserContext";
import { Dropdown } from "react-bootstrap";
import { FaSpinner } from "react-icons/fa";

const PreviewAttendeeModal = ({
  show,
  onHide,
  attendee = {},
  onEdit,
  onMoreAction,
  handleActionStatus,
}) => {
  const { sameState } = useUser();
  const [showProfile, setShowProfile] = useState(true);
  const [showDetails, setShowDetails] = useState(true);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [showOrderSummary, setShowOrderSummary] = useState(false);
  const [showAllTickets, setShowAllTickets] = useState(false);
  const [showPaymentDetails, setShowPaymentDetails] = useState(false);
  const [activeTab, setActiveTab] = useState("activity");
  const [noteName, setNoteName] = useState("");
  const [noteText, setNoteText] = useState("");
  const [eventDetails, setEventDetails] = useState([]);
  const [allTickets, setAllTickets] = useState([]);
  const [getEventStatus, setGetEventStatus] = useState(false);
  const [triggerDownload, setTriggerDownload] = useState(false);
  const invoiceRef = useRef(null);

  const getEventDetailsByUser = async () => {
    try {
      setGetEventStatus(true);
      const response = await axios.post(
        `${localserver}/attendee_details_by_id`,
        {
          event_id: attendee.event_id,
          attendee_id: attendee.id,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        }
      );
      if (response.data.success) {
        console.log("API Response:", response.data);
        setEventDetails(response.data || []);
      } else {
        console.error("Error: Failed to fetch user data");
        showToast("Failed to fetch events", "error");
      }
    } catch (error) {
      console.error("Error fetching events:", error);
      showToast("An error occurred while fetching events", "error");
    } finally {
      setGetEventStatus(false);
    }
  };

  useEffect(() => {
    if (show && attendee.id && attendee.event_id) {
      getEventDetailsByUser();
    } else if (show && attendee.isGuest) {
      // For guests, use passed data
      setEventDetails({
        data: [
          {
            form_data: JSON.stringify([
              { custom_fields: attendee.custom_fields },
            ]),
            reg_code: attendee.reg_code || "N/A",
            status: attendee.status || "confirmed",
            created_at: attendee.registeredOn || "",
            email: attendee.email || "N/A",
            first_name: attendee.first_name || "",
            last_name: attendee.last_name || "",
          },
        ],
      });
    }
  }, [show, attendee]);

  useEffect(() => {
    if (!eventDetails.data) return;

    const attendeeDataLocal = eventDetails.data[0] || {};
    const formFieldsLocal = parseFormData(attendeeDataLocal.form_data);
    const firstNameLocal =
      formFieldsLocal.find(
        (f) => f.name === "first_name" || f.label === "First Name"
      )?.value ||
      attendeeDataLocal.first_name ||
      "N/A";
    const lastNameLocal =
      formFieldsLocal.find(
        (f) => f.name === "last_name" || f.label === "Last Name"
      )?.value ||
      attendeeDataLocal.last_name ||
      "";
    const emailLocal =
      formFieldsLocal.find(
        (f) => f.name === "email_address" || f.label === "Email Address"
      )?.value ||
      attendeeDataLocal.email ||
      "N/A";

    let ticketDataLocal = {};
    if (attendeeDataLocal.ticket_data) {
      try {
        ticketDataLocal = JSON.parse(attendeeDataLocal.ticket_data);
      } catch (e) {
        console.error("Error parsing ticket_data for name:", e);
      }
    }
    const ticketName =
      ticketDataLocal.title ||
      ticketDataLocal.displayTitle ||
      ticketDataLocal.ticket_name ||
      "N/A";

    const paymentLocal = eventDetails.payments?.[0] || {};
    const ticketsLocal = [];

    let addons = [];
    if (
      attendeeDataLocal.add_ons_data &&
      attendeeDataLocal.add_ons_data !== "[]"
    ) {
      try {
        const parsedAddons = JSON.parse(attendeeDataLocal.add_ons_data);
        addons = parsedAddons
          .filter((a) => (a.quantity || 0) > 0)
          .map((a) => ({
            name: a.add_on_name || "N/A",
            quantity: a.quantity,
            price: a.price || 0,
          }));
      } catch (e) {
        console.error("Error parsing add_ons_data:", e);
      }
    }

    if (attendee.isGuest) {
      ticketsLocal.push({
        name: `${firstNameLocal} ${lastNameLocal}`.trim() || "Guest",
        ticket_name: "Accompanying Person",
        reg_code: attendeeDataLocal.reg_code || "N/A",
        order_id: attendee.orderId || "N/A",
        email: emailLocal,
        registered_on: formatDate(attendeeDataLocal.created_at),
        addons: [],
      });
    } else {
      ticketsLocal.push({
        name: `${firstNameLocal} ${lastNameLocal}`.trim(),
        ticket_name: ticketName,
        reg_code: attendeeDataLocal.reg_code || "N/A",
        order_id: paymentLocal.id || "N/A",
        email: emailLocal,
        registered_on: formatDate(attendeeDataLocal.created_at),
        addons,
      });

      let parsedGuests = [];
      if (
        attendeeDataLocal.guest_data &&
        attendeeDataLocal.guest_data !== "[]"
      ) {
        try {
          parsedGuests = JSON.parse(attendeeDataLocal.guest_data);
        } catch (e) {
          console.error("Error parsing guest_data:", e);
        }
      }

      parsedGuests.forEach((g) => {
        const gFirst =
          g.custom_fields.find(
            (f) => f.name === "first_name" || f.label === "First Name"
          )?.value || "";
        const gLast =
          g.custom_fields.find(
            (f) => f.name === "last_name" || f.label === "Last Name"
          )?.value || "";
        const gEmail =
          g.custom_fields.find(
            (f) => f.name === "email_address" || f.label === "Email Address"
          )?.value || "N/A";
        ticketsLocal.push({
          name: `${gFirst} ${gLast}`.trim() || "Guest",
          ticket_name: "Accompanying Person",
          reg_code: g.reg_code || "N/A",
          order_id: paymentLocal.id || "N/A",
          email: gEmail,
          registered_on: formatDate(attendeeDataLocal.created_at),
          addons: [],
        });
      });
    }

    setAllTickets(ticketsLocal);
  }, [eventDetails, attendee]);

  const handleDownloadInvoice = () => {
    if (!eventDetails.data) {
      console.error("No event details available for invoice");
      showToast("Cannot generate invoice: No event data", "error");
      return;
    }
    console.log("Triggering invoice download...");
    setTriggerDownload(true);
  };

  const handleDownloadCertificate = () => {
    const attendeeDataLocal = eventDetails.data?.[0];
    if (!attendeeDataLocal?.certificate) {
      showToast("No certificate available", "error");
      return;
    }

    console.log("Triggering certificate download...");
    let htmlContent = attendeeDataLocal.certificate;
    
    // Extract background URL from the HTML
    const bgUrlMatch = htmlContent.match(/background:url\(['"]([^'"]+)['"]\)/);
    const bgUrl = bgUrlMatch ? bgUrlMatch[1] : '';
    
    // Preload the background image
    const preloadImg = new Image();
    preloadImg.src = bgUrl;
    
    preloadImg.onload = () => {
      console.log('Background image loaded');
      const printWindow = window.open("", "_blank", "width=800,height=600");
      if (printWindow) {
        printWindow.document.write(`
          <!DOCTYPE html>
          <html>
          <head>
            <title>Certificate</title>
            <style>
              @media print {
                * {
                  -webkit-print-color-adjust: exact !important;
                  color-adjust: exact !important;
                  print-color-adjust: exact !important;
                }
                body {
                  margin: 0 !important;
                  padding: 0 !important;
                  -webkit-print-color-adjust: exact !important;
                }
                .certificate {
                  background-image: url('${bgUrl}') !important;
                  background-size: cover !important;
                  background-position: center !important;
                  background-repeat: no-repeat !important;
                  -webkit-print-color-adjust: exact !important;
                  width: 384px !important;
                  height: 576px !important;
                  margin: 0 auto !important;
                  page-break-inside: avoid !important;
                }
                .text-field {
                  -webkit-print-color-adjust: exact !important;
                  color: inherit !important;
                }
              }
              body {
                font-family: Arial, sans-serif;
                margin: 0;
                padding: 20px;
                background: white;
              }
              .certificate {
                position: relative;
                width: 384px;
                height: 576px;
                margin: 0 auto;
                border: 1px solid #ccc;
                border-radius: 8px;
                overflow: hidden;
              }
              .qr {
                position: absolute;
                left: 100px;
                top: 100px;
                width: 120px;
                height: 120px;
                pointer-events: none;
              }
              .text-field {
                position: absolute;
                padding: 10px 14px;
                font-size: 16px;
                font-family: 'Arial', sans-serif;
                font-weight: 700;
                color: #000000;
                text-align: center;
                background: transparent;
                border: none;
                outline: none;
                pointer-events: none;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                line-height: 1.2;
                width: 200px;
                height: 50px;
              }
            </style>
          </head>
          <body>
            ${htmlContent.replace('<body>', '').replace('</body></html>', '</body></html>')}
            <script>
              window.onload = function() {
                setTimeout(function() {
                  window.print();
                  // Close after print
                  setTimeout(function() {
                    window.close();
                  }, 1000);
                }, 500);
              };
            </script>
          </body>
          </html>
        `);
        printWindow.document.close();
      }
    };
    
    preloadImg.onerror = () => {
      console.error('Failed to load background image');
      // Fallback: proceed without preloading
      const printWindow = window.open("", "_blank", "width=800,height=600");
      if (printWindow) {
        printWindow.document.write(htmlContent);
        printWindow.document.close();
        setTimeout(() => {
          printWindow.print();
          setTimeout(() => printWindow.close(), 1000);
        }, 500);
      }
    };
  };

  const handleDownloadBadge = () => {
    const attendeeDataLocal = eventDetails.data?.[0];
    if (!attendeeDataLocal?.badge) {
      showToast("No badge available", "error");
      return;
    }

    console.log("Triggering badge download...");
    let htmlContent = attendeeDataLocal.badge;
    
    // Extract background URL from the HTML
    const bgUrlMatch = htmlContent.match(/background:url\(['"]([^'"]+)['"]\)/);
    const bgUrl = bgUrlMatch ? bgUrlMatch[1] : '';
    
    // Preload the background image
    const preloadImg = new Image();
    preloadImg.src = bgUrl;
    
    preloadImg.onload = () => {
      console.log('Background image loaded');
      const printWindow = window.open("", "_blank", "width=800,height=600");
      if (printWindow) {
        printWindow.document.write(`
          <!DOCTYPE html>
          <html>
          <head>
            <title>Badge</title>
            <style>
              @media print {
                * {
                  -webkit-print-color-adjust: exact !important;
                  color-adjust: exact !important;
                  print-color-adjust: exact !important;
                }
                body {
                  margin: 0 !important;
                  padding: 0 !important;
                  -webkit-print-color-adjust: exact !important;
                }
                .badge {
                  background-image: url('${bgUrl}') !important;
                  background-size: cover !important;
                  background-position: center !important;
                  background-repeat: no-repeat !important;
                  -webkit-print-color-adjust: exact !important;
                  width: 384px !important;
                  height: 576px !important;
                  margin: 0 auto !important;
                  page-break-inside: avoid !important;
                }
                .text-field {
                  -webkit-print-color-adjust: exact !important;
                  color: inherit !important;
                }
                .qr {
                  -webkit-print-color-adjust: exact !important;
                }
              }
              body {
                font-family: Arial, sans-serif;
                margin: 0;
                padding: 20px;
                background: white;
              }
              .badge {
                position: relative;
                width: 384px;
                height: 576px;
                margin: 0 auto;
                border: 1px solid #ccc;
                border-radius: 8px;
                overflow: hidden;
              }
              .qr {
                position: absolute;
                left: 132px;
                top: 334px;
                width: 119px;
                height: 110px;
                pointer-events: none;
              }
              .text-field {
                position: absolute;
                padding: 10px 14px;
                font-size: 16px;
                font-family: 'Arial', sans-serif;
                font-weight: 400;
                color: #333333;
                text-align: center;
                background: transparent;
                border: none;
                outline: none;
                pointer-events: none;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                line-height: 1.2;
                width: 200px;
                height: 50px;
              }
            </style>
          </head>
          <body>
            ${htmlContent.replace('<body>', '').replace('</body></html>', '</body></html>')}
            <script>
              window.onload = function() {
                setTimeout(function() {
                  window.print();
                  // Close after print
                  setTimeout(function() {
                    window.close();
                  }, 1000);
                }, 500);
              };
            </script>
          </body>
          </html>
        `);
        printWindow.document.close();
      }
    };
    
    preloadImg.onerror = () => {
      console.error('Failed to load background image');
      // Fallback: proceed without preloading
      const printWindow = window.open("", "_blank", "width=800,height=600");
      if (printWindow) {
        printWindow.document.write(htmlContent);
        printWindow.document.close();
        setTimeout(() => {
          printWindow.print();
          setTimeout(() => printWindow.close(), 1000);
        }, 500);
      }
    };
  };

  useEffect(() => {
    if (triggerDownload && invoiceRef.current) {
      invoiceRef.current.handleDownloadPDF();
      setTriggerDownload(false);
    }
  }, [triggerDownload]);

  const handleNoteSubmit = (e) => {
    e.preventDefault();
    console.log("Note Saved:", { noteName, noteText });
    // Add your save logic here (e.g., API call)
  };

  const parseFormData = (formData) => {
    try {
      const parsed = JSON.parse(formData || "[]");
      return parsed[0]?.custom_fields || [];
    } catch (error) {
      console.error("Error parsing form_data:", error);
      return [];
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString("en-US", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const attendeeData = eventDetails.data?.[0] || {};
  const formFields = parseFormData(attendeeData.form_data);
  const guestFormFields = parseFormData(attendeeData.guest_data) || [];
  let firstName =
    formFields.find((f) => f.name === "first_name" || f.label === "First Name")
      ?.value ||
    attendeeData.first_name ||
    "N/A";
  let lastName =
    formFields.find((f) => f.name === "last_name" || f.label === "Last Name")
      ?.value ||
    attendeeData.last_name ||
    "";
  let attendeeEmail =
    formFields.find(
      (f) => f.name === "email_address" || f.label === "Email Address"
    )?.value ||
    attendeeData.email ||
    "N/A";
  const ticket = eventDetails.tickets?.[0] || {};
  const payment = eventDetails.payments?.[0] || {};

  let ticketData = {};
  try {
    ticketData = JSON.parse(attendeeData.ticket_data || "{}");
  } catch (error) {
    console.error("Error parsing ticket_data:", error);
  }

  const sameStateLocal = eventDetails?.state_is_same || "yes";

  const getTicketTaxBreakdown = (ticketData, price, sameState) => {
    let taxOptions = ticketData.tax_options || [];
    if (typeof taxOptions === "string") {
      try {
        taxOptions = JSON.parse(taxOptions);
      } catch (error) {
        console.error("Error parsing tax_options:", error);
        taxOptions = [];
      }
    }
    const taxType = ticketData.tax_type || "No Tax";
    if (taxType === "No Tax") {
      return { taxes: [], taxLabel: "", totalTax: 0, base: price };
    }
    let breakdown = [];
    let totalTax = 0;
    const isInterState = sameState === "no";
    let rate = 0;
    if (isInterState) {
      const igstOption = taxOptions.find((t) => t.tax_name === "IGST");
      if (igstOption) {
        rate = igstOption.tax_percentage;
        let taxAmount =
          taxType === "Excluding Tax"
            ? price * (rate / 100)
            : price * (rate / (100 + rate));
        totalTax = taxAmount;
        breakdown = [{ name: "IGST", percentage: rate, amount: taxAmount }];
      }
    } else {
      const cgstOption = taxOptions.find((t) => t.tax_name === "CGST");
      const sgstOption = taxOptions.find((t) => t.tax_name === "SGST");
      if (cgstOption && sgstOption) {
        rate = cgstOption.tax_percentage + sgstOption.tax_percentage;
        let taxAmount =
          taxType === "Excluding Tax"
            ? price * (rate / 100)
            : price * (rate / (100 + rate));
        totalTax = taxAmount;
        const cgstAmt = (cgstOption.tax_percentage / rate) * taxAmount;
        const sgstAmt = taxAmount - cgstAmt;
        breakdown = [
          {
            name: "CGST",
            percentage: cgstOption.tax_percentage,
            amount: cgstAmt,
          },
          {
            name: "SGST",
            percentage: sgstOption.tax_percentage,
            amount: sgstAmt,
          },
        ];
      }
    }
    // fallback
    if (breakdown.length === 0 && rate === 0) {
      rate = 18;
      let taxAmount =
        taxType === "Excluding Tax"
          ? price * (rate / 100)
          : price * (rate / (100 + rate));
      totalTax = taxAmount;
      breakdown = [{ name: "GST", percentage: rate, amount: taxAmount }];
    }
    const taxLabel =
      breakdown.length > 0
        ? `(${breakdown.map((t) => `${t.name} ${t.percentage}%`).join("+")})`
        : "";
    const base = taxType === "Excluding Tax" ? price : price - totalTax;
    return { taxes: breakdown, taxLabel, totalTax, base };
  };

  // const getTaxBreakdown = (ticketData, payment, sameState) => {
  //   const taxOptions = ticketData.tax_options || [];
  //   const gstAmount = Number.parseFloat(payment.total_tax_amount || 0);
  //   const taxType = ticketData.tax_type || "No Tax";

  //   if (gstAmount <= 0 || taxOptions.length === 0 || taxType === "No Tax") {
  //     return { taxes: [], taxLabel: "" };
  //   }

  //   let taxes = [];
  //   let taxLabel = "";

  //   if (taxType === "Including Tax" || taxType === "Excluding Tax") {
  //     // Determine tax structure based on state
  //     const isInterState = sameState === "no";

  //     if (isInterState) {
  //       // Inter-state: Use IGST
  //       const igstOption = taxOptions.find((t) => t.tax_name === "IGST");
  //       if (igstOption) {
  //         taxes = [
  //           {
  //             name: "IGST",
  //             percentage: igstOption.tax_percentage,
  //             amount: gstAmount,
  //           },
  //         ];
  //         taxLabel = `(IGST ${igstOption.tax_percentage}%)`;
  //       }
  //     } else {
  //       // Intra-state: Use CGST + SGST
  //       const cgstOption = taxOptions.find((t) => t.tax_name === "CGST");
  //       const sgstOption = taxOptions.find((t) => t.tax_name === "SGST");

  //       if (cgstOption && sgstOption) {
  //         const totalRate =
  //           cgstOption.tax_percentage + sgstOption.tax_percentage;
  //         const cgstAmount =
  //           (cgstOption.tax_percentage / totalRate) * gstAmount;
  //         const sgstAmount =
  //           (sgstOption.tax_percentage / totalRate) * gstAmount;

  //         taxes = [
  //           {
  //             name: "CGST",
  //             percentage: cgstOption.tax_percentage,
  //             amount: cgstAmount,
  //           },
  //           {
  //             name: "SGST",
  //             percentage: sgstOption.tax_percentage,
  //             amount: sgstAmount,
  //           },
  //         ];
  //         taxLabel = `(CGST ${cgstOption.tax_percentage}% + SGST ${sgstOption.tax_percentage}%)`;
  //       }
  //     }
  //   }

  //   // Fallback if no proper tax configuration
  //   if (taxes.length === 0 && gstAmount > 0) {
  //     const gstOption = taxOptions.find((t) => t.tax_name === "GST");
  //     const percentage = gstOption?.tax_percentage || 18;
  //     taxes = [{ name: "GST", percentage, amount: gstAmount }];
  //     taxLabel = `(GST ${percentage}%)`;
  //   }

  //   return { taxes, taxLabel };
  // };

  const getTaxBreakdown = (ticketData, payment, sameState) => {
    let taxOptions = ticketData.tax_options || [];
    // Ensure taxOptions is always an array to prevent .find() errors
    if (!Array.isArray(taxOptions)) {
      console.warn("taxOptions is not an array:", taxOptions);
      taxOptions = [];
    }
    const gstAmount = Number.parseFloat(payment.total_tax_amount || 0);
    const taxType = ticketData.tax_type || "No Tax";  
  
    if (gstAmount <= 0 || taxOptions.length === 0 || taxType === "No Tax") {
      console.log("****taxOptions: ", taxOptions);
      return { taxes: [], taxLabel: "" };
    }
  
    let taxes = [];
    let taxLabel = "";
  
    if (taxType === "Including Tax" || taxType === "Excluding Tax") {
      // Determine tax structure based on state
      const isInterState = sameState === "no";
  
      if (isInterState) {
        // Inter-state: Use IGST
        const igstOption = taxOptions.find((t) => t.tax_name === "IGST");
        if (igstOption) {
          taxes = [
            {
              name: "IGST",
              percentage: igstOption.tax_percentage,
              amount: gstAmount,
            },
          ];
          taxLabel = `(IGST ${igstOption.tax_percentage}%)`;
        }
      } else {
        // Intra-state: Use CGST + SGST
        const cgstOption = taxOptions.find((t) => t.tax_name === "CGST");
        const sgstOption = taxOptions.find((t) => t.tax_name === "SGST");
  
        if (cgstOption && sgstOption) {
          const totalRate =
            cgstOption.tax_percentage + sgstOption.tax_percentage;
          const cgstAmount =
            (cgstOption.tax_percentage / totalRate) * gstAmount;
          const sgstAmount =
            (sgstOption.tax_percentage / totalRate) * gstAmount;
  
          taxes = [
            {
              name: "CGST",
              percentage: cgstOption.tax_percentage,
              amount: cgstAmount,
            },
            {
              name: "SGST",
              percentage: sgstOption.tax_percentage,
              amount: sgstAmount,
            },
          ];
          taxLabel = `(CGST ${cgstOption.tax_percentage}% + SGST ${sgstOption.tax_percentage}%)`;
        }
      }
    }
  
    // Fallback if no proper tax configuration
    if (taxes.length === 0 && gstAmount > 0) {
      const gstOption = taxOptions.find((t) => t.tax_name === "GST");
      const percentage = gstOption?.tax_percentage || 18;
      taxes = [{ name: "GST", percentage, amount: gstAmount }];
      taxLabel = `(GST ${percentage}%)`;
    }
  
    return { taxes, taxLabel };
  };

  const { taxes, taxLabel } = getTaxBreakdown(
    ticketData,
    payment,
    eventDetails.state_is_same
  );

  const ticketPrice = Number.parseFloat(payment.total_ticket_amount || 0);
  const totalAmount = Number.parseFloat(payment.total_amount || 0);
  const dueAmount =
    totalAmount - Number.parseFloat(payment.payment_received || 0);
  const currency = payment.currency || "INR";
  const addonsAmount = Number.parseFloat(payment.total_addons_amount || 0);
  const subtotal = ticketPrice + addonsAmount;
  const couponAmount = Number.parseFloat(payment.coupon_applied_amount || 0);
  const couponPercentage = payment.coupon_applied_percentage || "0";
  const discountedSubtotal = subtotal - couponAmount;
  if (attendee.isGuest) {
    firstName =
      attendee.custom_fields.find(
        (f) => f.name === "first_name" || f.label === "First Name"
      )?.value ||
      attendee.first_name ||
      "N/A";
    lastName =
      attendee.custom_fields.find(
        (f) => f.name === "last_name" || f.label === "Last Name"
      )?.value ||
      attendee.last_name ||
      "";
    attendeeEmail =
      attendee.custom_fields.find(
        (f) => f.name === "email_address" || f.label === "Email Address"
      )?.value ||
      attendee.email ||
      "N/A";
  }

  return (
    <>
      <ToastMessage />

      {/* Attendee/Guest Modal */}
      {!showOrderModal && (
        <div
          className={`extraspace_search_divattendies add_attendites_model member_master_sidebar_scrool search_div${
            show ? " open" : ""
          }`}
        >
          <div className="card mb-3">
            <div className="member_edit_pop_flex">
              <h3>{attendee.isGuest ? "Preview Guest" : "Preview Attendee"}</h3>
              <button
                className="search_toggle btn btn-danger"
                onClick={() => {
                  setTriggerDownload(false);
                  onHide();
                }}
              >
                <i className="fa fa-times" />
              </button>
            </div>
          </div>

          {getEventStatus ? (
            <div className="text-center p-5">
              <i className="fa fa-spinner fa-spin fa-3x"></i>
              <p className="mt-3">Loading event details...</p>
            </div>
          ) : (
            <div className="card">
              <div className="body mb-3">
                <div className="top-section">
                  <div className="inner_section_top">
                    {/* Attendee/Guest Info Toggle */}
                    <button
                      className={`attendies_button_flex mtb-2 ${
                        showProfile ? "active" : ""
                      }`}
                      onClick={() => setShowProfile(!showProfile)}
                    >
                      {showProfile ? (
                        <i className="fa fa-angle-down"></i>
                      ) : (
                        <i className="fa fa-angle-up"></i>
                      )}{" "}
                      {attendee.isGuest ? "Guest Info" : "Attendee Info"}
                    </button>

                    {showProfile && (
                      <div className="profile-card">
                        <div className="profile-header">
                          <div className="avatar">
                            <i className="fa fa-building-o"></i>
                          </div>
                          <div className="profile-info">
                            <h3>{`${firstName} ${lastName}`.trim()}</h3>
                            <div className="registration">
                              <span className="status-confirmed">
                                ●{" "}
                                {attendeeData.status?.charAt(0).toUpperCase() +
                                  attendeeData.status?.slice(1) || "Confirmed"}
                              </span>
                            </div>
                            <div className="info-lines">
                              <div className="ptb-5">
                                Order ID:{" "}
                                <a className="link">
                                  <span
                                    className="font-weight-bold text-col-blue cursor-pointer"
                                    onClick={() => {
                                      setShowOrderModal(true);
                                    }}
                                  >
                                    {payment.id || "View Order/payments"}
                                  </span>
                                </a>
                              </div>
                              <div className="ptb-5">
                                Tickets:{" "}
                                <span className="font-weight-bold text-col-blue">
                                  {attendee.isGuest
                                    ? "Accompanying Person"
                                    : ticketData.title ||
                                      ticketData.displayTitle ||
                                      ticketData.ticket_name ||
                                      "N/A"}
                                </span>
                              </div>
                              <div className="ptb-5">
                                Registered on:{" "}
                                <span className="font-weight-bold text-col-blue">
                                  {formatDate(attendeeData.created_at)}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="profile-actions">
                          <button
                            className="btn btn-outline-primary"
                            onClick={() => {
                              onHide();
                              onEdit(attendee);
                            }}
                          >
                            {attendee.isGuest ? "Edit Guest" : "Edit Attendee"}
                          </button>
                          {attendeeData.status !== "pending" && (
                            <button
                              className="btn btn-outline-primary"
                              disabled={handleActionStatus}
                              onClick={() => {
                                onMoreAction(attendeeData, "resend");
                              }}
                            >
                              {handleActionStatus ? (
                                <>
                                  <FaSpinner
                                    className="fa-spin"
                                    style={{ fontSize: "14px" }}
                                  />{" "}
                                  Sending...
                                </>
                              ) : (
                                "Resend Success Email"
                              )}
                            </button>
                          )}
                          {/* <Dropdown>
                            <Dropdown.Toggle className="btn btn-primary">
                              More Actions <i className="fa fa-angle-down"></i>
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                              <Dropdown.Item onClick={() => onMoreAction(attendeeData, "cancel")}>
                                Resend Success Email
                              </Dropdown.Item>
                              <Dropdown.Item onClick={() => onMoreAction(attendeeData, "resend")}>
                                Cancel Ticket
                              </Dropdown.Item>
                            </Dropdown.Menu>
                          </Dropdown> */}
                        </div>
                      </div>
                    )}

                    {/* Details Toggle */}
                    <button
                      className={`attendies_button_flex ${
                        showDetails ? "active" : ""
                      }`}
                      onClick={() => setShowDetails(!showDetails)}
                    >
                      {showDetails ? (
                        <i className="fa fa-angle-down"></i>
                      ) : (
                        <i className="fa fa-angle-up"></i>
                      )}{" "}
                      Details
                    </button>

                    {showDetails && !attendee.isGuest && (
                      <div className="details-card">
                        <h4>Details</h4>
                        <div className="details-grid">
                          {formFields.map((field, index) => (
                            <div key={index}>
                              <label>{field?.label}</label>
                              <p
                                style={{
                                  margin: "0 0 8px 0",
                                  fontSize: "14px",
                                }}
                              >
                                {field.type === "file" && field?.value ? (
                                  <img
                                    src={field?.value}
                                    alt={`Uploaded image ${index + 1}`}
                                    style={{
                                      maxWidth: "100px",
                                      marginTop: "5px",
                                      marginRight: "10px",
                                    }}
                                  />
                                ) : field.type === "password" ? (
                                  <p>********</p>
                                ) : field?.value ? (
                                  <p>{field?.value}</p>
                                ) : (
                                  <p>{"N/A"}</p>
                                )}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {showDetails && attendee.isGuest && (
                      <div className="details-card">
                        <h4>Details</h4>
                        <div className="details-grid">
                          {/* {guestFormFields.map((field, index) => (
                            <div key={index}>
                              <label>{field?.label}</label>
                              <p>{field?.value || "N/A"}</p>
                            </div>
                          ))} */}
                          {guestFormFields.map((field, index) => (
                            <div key={index}>
                              <label>{field?.label}</label>
                              <p
                                style={{
                                  margin: "0 0 8px 0",
                                  fontSize: "14px",
                                }}
                              >
                                {field.type === "file" ? (
                                  <img
                                    src={field?.value}
                                    alt={`Uploaded image ${index + 1}`}
                                    style={{
                                      maxWidth: "100px",
                                      marginTop: "5px",
                                      marginRight: "10px",
                                    }}
                                  />
                                ) : field.type === "password" ? (
                                  <p>********</p>
                                ) : field?.value ? (
                                  <p>{field?.value}</p>
                                ) : (
                                  <p>{"N/A"}</p>
                                )}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Email Communication */}
                  {!attendee.isGuest && (
                    <div className="email-card">
                      <div className="inner_mail_div">
                        <h4>Email Communications</h4>
                        <div className="email-item">
                          <span className="icon">
                            <i className="icon-envelope"></i>
                          </span>{" "}
                          Registration {payment.payment_status}
                          <div className="email-date">
                            Opened · {formatDate(payment.created_at)}
                          </div>
                        </div>
                        <div className="email-item">
                          <span className="icon">
                            <i className="icon-envelope"></i>
                          </span>{" "}
                          Order {payment.payment_status}
                          <div className="email-date">
                            Opened · {formatDate(payment.payment_date)}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Order Modal */}
      {showOrderModal && (
        <div
          className={`extraspace_search_divattendies_view add_attendites_model member_master_sidebar_scrool search_div open`}
        >
          <div className="card mb-3">
            <div className="member_edit_pop_flex">
              <h3>Order Details - #{payment.id || "N/A"}</h3>
              <button
                className="search_toggle btn btn-danger"
                onClick={() => {
                  setTriggerDownload(false);
                  setShowOrderModal(false);
                }}
              >
                <i className="fa fa-arrow-left" /> Back
              </button>
            </div>
          </div>
          {getEventStatus ? (
            <div className="text-center p-5">
              <i className="fa fa-spinner fa-spin fa-3x"></i>
              <p className="mt-3">Loading event details...</p>
            </div>
          ) : (
            <div className="card">
              <div className="body">
                <div className="top-section">
                  <div className="inner_section_top">
                    {/* Attendee Info Toggle */}
                    <button
                      className={`attendies_button_flex mtb-2 ${
                        showProfile ? "active" : ""
                      }`}
                      onClick={() => setShowProfile(!showProfile)}
                    >
                      {showProfile ? (
                        <i className="fa fa-angle-down"></i>
                      ) : (
                        <i className="fa fa-angle-up"></i>
                      )}{" "}
                      Attendee Info
                    </button>

                    {showProfile && (
                      <div className="profile-card">
                        <div className="profile-header">
                          <div className="avathor_flex_div">
                            <div className="avatar">
                              <i className="fa fa-building-o"></i>
                            </div>
                            <div className="profile-info">
                              <h3>{`${firstName} ${lastName}`.trim()}</h3>
                              <div className="registration">
                                <span className="status-confirmed">
                                  ●{" "}
                                  {attendeeData.status
                                    ?.charAt(0)
                                    .toUpperCase() +
                                    attendeeData.status?.slice(1) ||
                                    "Confirmed"}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="details-card">
                            <h4>Details</h4>
                            <div className="details-grid">
                              {formFields.map((field, index) => (
                                <div key={index}>
                                  <label>{field?.label}</label>
                                  <p
                                    style={{
                                      margin: "0 0 8px 0",
                                      fontSize: "14px",
                                    }}
                                  >
                                    {field.type === "file" && field?.value ? (
                                      <img
                                        src={field?.value}
                                        alt={`Uploaded image ${index + 1}`}
                                        style={{
                                          maxWidth: "100px",
                                          marginTop: "5px",
                                          marginRight: "10px",
                                        }}
                                      />
                                    ) : field.type === "password" ? (
                                      <p>********</p>
                                    ) : field?.value ? (
                                      <p>{field?.value}</p>
                                    ) : (
                                      <p>{"N/A"}</p>
                                    )}
                                  </p>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Order Details Toggle */}
                    <div className="order-card-section">
                      <button
                        className={`attendies_button_flex ${
                          showDetails ? "active" : ""
                        }`}
                        onClick={() => setShowDetails(!showDetails)}
                      >
                        {showDetails ? (
                          <i className="fa fa-angle-down" />
                        ) : (
                          <i className="fa fa-angle-up" />
                        )}{" "}
                        <div className="order_id_flex">
                          <div>Order ID: {payment.id || "N/A"}</div>
                        </div>
                      </button>

                      {showDetails && (
                        <div className="details_page_card mt-3">
                          <div className="order_head_flex">
                            <div>
                              <div className="sub_div">
                                {allTickets.length} ticket
                              </div>
                            </div>
                            <div>
                              <h5 className="mb-1">
                                {payment.currency || "INR"}{" "}
                                {totalAmount.toFixed(2)}
                              </h5>
                              <small className="text-success">
                                ● {payment.payment_status || "Completed"}
                              </small>
                            </div>
                          </div>

                          <div>
                            <h4>Order Details</h4>
                            <div className="details-grid details-card">
                              <div>
                                <label>Order Total</label>
                                <p>
                                  {payment.currency || "INR"}{" "}
                                  {totalAmount.toFixed(2)}
                                </p>
                              </div>
                              <div>
                                <label>Buyer</label>
                                <p>{`${firstName} ${lastName}`.trim()}</p>
                              </div>
                              <div>
                                <label>Amount Paid</label>
                                <p>
                                  {payment.currency || "INR"}{" "}
                                  {Number.parseFloat(
                                    payment.payment_received || 0
                                  ).toFixed(2)}
                                </p>
                              </div>
                              <div>
                                <label>Amount Due</label>
                                <p>
                                  {payment.currency || "INR"}{" "}
                                  {dueAmount.toFixed(2)}
                                </p>
                              </div>
                              <div>
                                <label>Order Initiated on</label>
                                <p>{formatDate(payment.created_at)}</p>
                              </div>
                              <div>
                                <label>Order Completed on</label>
                                <p>{formatDate(payment.payment_date)}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Order Summary Toggle */}
                    <button
                      className={`attendies_button_flex mtb-2 ${
                        showOrderSummary ? "active" : ""
                      }`}
                      onClick={() => setShowOrderSummary(!showOrderSummary)}
                    >
                      {showOrderSummary ? (
                        <i className="fa fa-angle-down"></i>
                      ) : (
                        <i className="fa fa-angle-up"></i>
                      )}{" "}
                      Order Summary
                    </button>

                    {showOrderSummary && (
                      <div className="profile-card">
                        <div className="d-flex justify-content-between align-items-center mb-3"> 
                          <h4 className="mb-0">Order Summary</h4>
                        </div>
                        <div className="d-flex flex-wrap gap-2 cancel_order_flex">
                            {(attendeeData.status !== "pending" && payment.order_status !== "cancelled") && (
                              <button
                                className="btn btn-danger btn-sm"
                                onClick={() =>
                                  onMoreAction(attendeeData, "cancel")
                                }
                              >
                                Cancel Order
                              </button>
                            )}
                            {attendeeData.status !== "pending" &&
                            <button
                              className="btn btn-outline-primary btn-sm"
                              onClick={handleDownloadInvoice}
                            >
                              <i className="fa fa-download me-1" /> {"Invoice"}
                            </button>}
                            {attendeeData.certificate && attendeeData.status !== "pending" && (
                              <button
                                className="btn btn-outline-primary btn-sm"
                                onClick={handleDownloadCertificate}
                              >
                                <i className="fa fa-download me-1" /> {"Certificate"}
                              </button>
                            )}
                            {attendeeData.badge && attendeeData.status !== "pending" && (
                              <button
                                className="btn btn-outline-primary btn-sm"
                                onClick={handleDownloadBadge}
                              >
                                <i className="fa fa-download me-1" /> {"Badge"}
                              </button>
                            )}
                          </div>
                        <div className="details-card">
                          <div className="order-summary-body">
                            <div className="d-flex justify-content-between mb-2">
                              <div className="title_div">Item</div>
                              <div className="title_div">Amount</div>
                            </div>

                            {/* Ticket Details */}
                            <div className="d-flex justify-content-between text-muted mb-2">
                              <div className="sub_div">
                                1 ×{" "}
                                {ticketData.title ||
                                  ticketData.displayTitle ||
                                  ticketData.ticket_name ||
                                  "Event Ticket"}
                                {ticketData.tax_type && (
                                  <div className="text-xs text-muted mt-1">
                                    Tax Type: {ticketData.tax_type}
                                  </div>
                                )}
                              </div>
                              <div className="sub_div">
                                {payment.currency || "INR"}{" "}
                                {(payment.ticket_count > 0
                                  ? Number(payment.total_ticket_amount) /
                                    payment.ticket_count
                                  : 0
                                ).toFixed(2)}
                              </div>
                            </div>

                            {/* Add-ons details */}
                            {attendeeData.add_ons_data &&
                              attendeeData.add_ons_data !== "[]" &&
                              (() => {
                                try {
                                  const parsedAddons = JSON.parse(
                                    attendeeData.add_ons_data
                                  );
                                  return parsedAddons
                                    .map((addon, idx) => {
                                      const qty = Number(addon.quantity) || 0;
                                      if (qty <= 0) return null;
                                      const price = Number(addon.price) || 0;
                                      const { base } = getTicketTaxBreakdown(
                                        addon,
                                        price,
                                        sameStateLocal
                                      );
                                      const total = base * qty;
                                      return (
                                        <div
                                          key={idx}
                                          className="d-flex justify-content-between text-muted mb-2"
                                        >
                                          <div className="sub_div">
                                            {qty} ×{" "}
                                            {addon.add_on_name || "Add-on"} (
                                            {currency}
                                            {price.toFixed(2)})
                                            {addon.tax_type &&
                                              addon.tax_type !== "No Tax" && (
                                                <div className="text-xs text-muted mt-1">
                                                  Tax Type: {addon.tax_type}
                                                </div>
                                              )}
                                          </div>
                                          <div className="sub_div">
                                            {currency} {total.toFixed(2)}
                                          </div>
                                        </div>
                                      );
                                    })
                                    .filter(Boolean);
                                } catch (e) {
                                  console.error(
                                    "Error parsing add_ons_data:",
                                    e
                                  );
                                  return null;
                                }
                              })()}

                            <hr />

                            {/* Subtotal */}
                            <div className="d-flex justify-content-between text-muted mb-1">
                              <div className="title_div">Subtotal</div>
                              <div className="title_div">
                                {payment.currency || "INR"}{" "}
                                {subtotal.toFixed(2)}
                              </div>
                            </div>

                            {/* Discount (if applied) */}
                            {couponAmount > 0 && (
                              <div className="d-flex justify-content-between text-muted mb-1">
                                <div className="sub_div">
                                  Discount ({couponPercentage}%)
                                </div>
                                <div className="sub_div">
                                  -{currency} {couponAmount.toFixed(2)}
                                </div>
                              </div>
                            )}

                            {/* Subtotal after discount */}
                            {couponAmount > 0 && (
                              <div className="d-flex justify-content-between text-muted mb-1">
                                <div className="title_div">
                                  Subtotal after discount
                                </div>
                                <div className="title_div">
                                  {currency} {discountedSubtotal.toFixed(2)}
                                </div>
                              </div>
                            )}

                            {/* Tax Breakdown */}
                            {taxes.length > 0 && (
                              <>
                                {taxes.map((tax, idx) => (
                                  <div
                                    key={idx}
                                    className="d-flex justify-content-between text-muted mb-1"
                                  >
                                    <div className="sub_div">
                                      {tax.name} @ {tax.percentage}%
                                    </div>
                                    <div className="sub_div">
                                      {payment.currency || "INR"}{" "}
                                      {tax.amount.toFixed(2)}
                                    </div>
                                  </div>
                                ))}
                                <div className="d-flex justify-content-between text-muted mb-3">
                                  <div className="sub_div font-weight-bold">
                                    Total Tax {taxLabel}
                                  </div>
                                  <div className="sub_div font-weight-bold">
                                    {payment.currency || "INR"}{" "}
                                    {taxes
                                      .reduce((acc, tax) => acc + tax.amount, 0)
                                      .toFixed(2)}
                                  </div>
                                </div>
                              </>
                            )}

                            <hr />

                            {/* Order Total */}
                            <div className="d-flex justify-content-between fw-bold">
                              <div className="title_div">Order Total</div>
                              <div className="sub_div">
                                {payment.currency || "INR"}{" "}
                                {totalAmount.toFixed(2)}
                              </div>
                            </div>

                            {/* Payment Status */}
                            <div className="mt-3 pt-2 border-top">
                              <div className="d-flex justify-content-between text-muted mb-1">
                                <div className="sub_div">Amount Paid</div>
                                <div className="sub_div text-success">
                                  {payment.currency || "INR"}{" "}
                                  {Number.parseFloat(
                                    payment.payment_received || 0
                                  ).toFixed(2)}
                                </div>
                              </div>
                              {dueAmount > 0 && (
                                <div className="d-flex justify-content-between text-muted">
                                  <div className="sub_div">Amount Due</div>
                                  <div className="sub_div text-danger">
                                    {payment.currency || "INR"}{" "}
                                    {dueAmount.toFixed(2)}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* All Tickets Toggle */}
                    <button
                      className={`attendies_button_flex mtb-2 ${
                        showAllTickets ? "active" : ""
                      }`}
                      onClick={() => setShowAllTickets(!showAllTickets)}
                    >
                      {showAllTickets ? (
                        <i className="fa fa-angle-down"></i>
                      ) : (
                        <i className="fa fa-angle-up"></i>
                      )}{" "}
                      All Tickets ({allTickets.length})
                    </button>

                    {showAllTickets &&
                      allTickets.map((item, index) => (
                        <div key={index} className="profile-card">
                          <h4>{item.name}</h4>
                          <div className="details-card p-0">
                            <div className="order-summary-body p-0 border rounded shadow-sm overflow-Hidden">
                              <div className="px-3 pt-3 pb-2">
                                <p className="mb-2 sub_div">
                                  {item.ticket_name}
                                </p>
                                {item.addons && item.addons.length > 0 && (
                                  <div className="mb-3">
                                    <p className="text-muted small mb-1">
                                      Add-ons:
                                    </p>
                                    {item.addons.map((ad, adIdx) => (
                                      <p key={adIdx} className="sub_div mb-0">
                                        {ad.name} × {ad.quantity}
                                      </p>
                                    ))}
                                  </div>
                                )}
                                <hr className="my-2" />
                                <div className="d-flex justify-content-between flex-wrap text-muted pb-3">
                                  <div className="mb-2">
                                    <div className="title_div">
                                      Registration ID
                                    </div>
                                    <div className="sub_div">
                                      {item.reg_code}
                                    </div>
                                  </div>
                                  <div className="mb-2">
                                    <div className="title_div">Order ID</div>
                                    <div className="sub_div">
                                      {item.order_id}
                                    </div>
                                  </div>
                                  <div className="mb-2">
                                    <div className="title_div">Email ID</div>
                                    <div className="sub_div">{item.email}</div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}

                    {/* Payment Details Toggle */}
                    <button
                      className={`attendies_button_flex mtb-2 ${
                        showPaymentDetails ? "active" : ""
                      }`}
                      onClick={() => setShowPaymentDetails(!showPaymentDetails)}
                    >
                      {showPaymentDetails ? (
                        <i className="fa fa-angle-down"></i>
                      ) : (
                        <i className="fa fa-angle-up"></i>
                      )}{" "}
                      Payment Info
                    </button>

                    {showPaymentDetails && (
                      <div className="profile-card">
                        <div className="payment-card">
                          <div
                            className={`payment-status ${
                              payment.payment_status === "Success"
                                ? "text-success"
                                : "text-danger"
                            }`}
                          >
                            {payment.payment_status === "Success" && (
                              <i className="fa fa-check-circle"></i>
                            )}
                            <span>
                              Payment {payment.payment_status || "pending"}
                            </span>
                          </div>
                          <div className="payment-grid">
                            <div className="payment-item">
                              <span>Amount Paid</span>
                              <span className="paid">
                                {payment.currency || "INR"}{" "}
                                {Number.parseFloat(
                                  payment.payment_received || 0
                                ).toFixed(2)}
                              </span>
                            </div>
                            <div className="payment-item">
                              <span>Payment Date</span>
                              <span>{formatDate(payment.payment_date)}</span>
                            </div>
                            <div className="payment-item">
                              <span>Method</span>
                              {Number.parseFloat(payment.payment_received) >
                              0 ? (
                                <span>
                                  {payment.payment_method === "Razorpay"
                                    ? "Online"
                                    : payment.payment_method || "Online"}
                                </span>
                              ) : (
                                <span>{"FREE"}</span>
                              )}
                            </div>
                            <div className="payment-item">
                              <span>Gateway</span>
                              {Number.parseFloat(payment.payment_received) >
                              0 ? (
                                <span>
                                  {payment.payment_method === "Razorpay" ? "Razorpay" : "Cheque"}
                                </span>
                              ) : (
                                <span>{"-"}</span>
                              )}
                            </div>
                            {payment.payment_id && (
                              <div className="payment-item full-width">
                                <span>Reference ID</span>
                                <span className="ref-id">
                                  {payment.payment_id}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="email-card">
                    <div className="activity-notes-wrapper">
                      <div className="tabs-toggle tab-buttons mb-4 pillsContainer">
                        <button
                          className={`pill ${
                            activeTab === "activity" ? "activePill" : ""
                          }`}
                          onClick={() => setActiveTab("activity")}
                        >
                          Activity Log
                        </button>
                      </div>
                      {activeTab === "activity" && (
                        <div className="activity-log">
                          <div key={payment.id}>
                            {/* Order confirmed/completed */}
                            <div className="activity-item completed">
                              <div className="icon-circle success">
                                <i className="icon-check"></i>
                              </div>
                              <div className="activity-content">
                                <div className="activity-title">
                                  {payment.order_status === "confirmed"
                                    ? "Order Confirmed"
                                    : "Order Completed"}
                                </div>
                                <div className="activity-time">
                                  {formatDate(payment.payment_date)}
                                </div>
                              </div>
                            </div>

                            {/* Payment */}
                            <div className="activity-item info">
                              <div className="icon-circle info">
                                <i className="icon-info"></i>
                              </div>
                              <div className="activity-content">
                                <div className="activity-title">
                                  Payment {payment.payment_status}
                                </div>
                                <div className="activity-time">
                                  {formatDate(payment.payment_date)}
                                </div>
                              </div>
                            </div>

                            {/* Order initiation */}
                            <div className="activity-item info">
                              <div className="icon-circle info">
                                <i className="icon-info"></i>
                              </div>
                              <div className="activity-content">
                                <div className="activity-title">
                                  Order Initiated
                                </div>
                                <div className="activity-time">
                                  {formatDate(payment.created_at)}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                      {/* {activeTab === "activity" && (
                        <div className="activity-log">
                          <div className="activity-item completed">
                            <div className="icon-circle success">
                              <i className="icon-check"></i>
                            </div>
                            <div className="activity-content">
                              <div className="activity-title">Order completed</div>
                              <div className="activity-time">{formatDate(payment.payment_date)}</div>
                            </div>
                          </div>
                          <div className="activity-item info">
                            <div className="icon-circle info">
                              <i className="icon-info"></i>
                            </div>
                            <div className="activity-content">
                              <div className="activity-title">Payment Completed</div>
                              <div className="activity-time">{formatDate(payment.payment_date)}</div>
                            </div>
                          </div>
                          <div className="activity-item info">
                            <div className="icon-circle info">
                              <i className="icon-info"></i>
                            </div>
                            <div className="activity-content">
                              <div className="activity-title">Order initiated</div>
                              <div className="activity-time">{formatDate(payment.created_at)}</div>
                            </div>
                          </div>
                        </div>
                      )} */}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          {triggerDownload && (
            <div style={{ position: "absolute", left: "-9999px" }}>
              <EventInvoice
                ref={invoiceRef}
                invoiceData={eventDetails}
                autoDownload={true}
              />
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default PreviewAttendeeModal;